import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  cultureHover,
  getCultureGrossMargin,
  loadResultMapTabData,
  parcelHoverCultureOpacity,
  sortList,
} from 'utils/Map/MapTabUtils';
import { getColorByName } from 'utils/culturesColors';
import { ExploitationContext } from 'utils/context';
import YearsMapTab from './YearsMapTab';
import { getPercentage } from 'utils/tools_functions';
import './CulturesMapTab.style.scss';
import bioLogo from 'assets/images/bio.png';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import CheckIcon from '@mui/icons-material/Check';

const noAddedItems = [];

const getContrastColor = (hex) => {
  const value = parseInt(hex.replace('#', ''), 16);
  const brightness =
    ((value >> 16) * 299 + ((value >> 8) & 255) * 587 + (value & 255) * 114) / 1000;
  return brightness > 150 ? '#1b1b1b' : 'white';
};

const MapTab = ({
  data,
  header,
  isDynamic,
  year,
  setYear,
  minYear,
  maxYear,
  yearIndex = null,
  suggestions = null,
  parcelHoverCultureName = null,
  parentCallback = null,
  previousCropMargin = null,
  fromProduction = false,
  fromSynthesis = false,
  fromParcels = false,
  selectedCultureId = null,
  onSelectCulture = null,
  addedItems = noAddedItems,
  footer = null,
  colorsByName = null,
  hideYears = false,
  hideSubtotals = false,
}) => {
  const { culturesColors } = useContext(ExploitationContext);
  const [culturesByYear, setCulturesByYear] = useState([]);
  const [totalSurface, setTotalSurface] = useState(0);
  const { t } = useTranslation();
  const getColor = (name) => colorsByName?.[name] ?? getColorByName(name, culturesColors);

  useEffect(() => {
    if (data) {
      const { allCulturesByYear, totalSurfaceIncrement } = loadResultMapTabData(
        data,
        culturesColors,
        minYear,
        maxYear,
        fromParcels ? true : false,
      );

      if (fromParcels && addedItems.length) {
        for (let y = minYear - 1; y <= maxYear - 1; y++) {
          if (!allCulturesByYear.some((e) => e.year === y)) {
            allCulturesByYear.push({ year: y, cultures: [], totalArableSurface: 0 });
          }
        }
        allCulturesByYear.forEach((yearData) => {
          addedItems.forEach((culture) => {
            if (!yearData.cultures.some((c) => c.id === culture.id)) {
              yearData.cultures.push({
                name: culture.name,
                color: getColor(culture.name),
                year: yearData.year,
                surface: 0,
                id: culture.id,
                organicState: culture.organicState,
                type: culture.type,
              });
            }
          });
        });
      }

      allCulturesByYear.forEach((yearData) => {
        const distribution = [
          ...(previousCropMargin ?? []),
          ...(suggestions?.flatMap((s) => s.culturesDistribution ?? []) ?? []),
        ];
        yearData.cultures = yearData.cultures.map((culture) => ({
          ...culture,
          capCategory: distribution.find((d) => d.culture?.id === culture.id)?.culture?.capCategory,
        }));
      });

      setCulturesByYear(allCulturesByYear);
      setTotalSurface(totalSurfaceIncrement);
    }
  }, [data, year, yearIndex, addedItems]);

  return (
    <div className="cultures-map-tab-container">
      {!fromProduction && !hideYears && (
        <YearsMapTab year={year} minYear={minYear} maxYear={maxYear} setYear={setYear} />
      )}
      <div
        className={
          fromProduction ? 'table-container sticky-table table-production' : 'table-container'
        }
      >
        <div className="table-wrapper">
          {!culturesByYear?.some((culture) => culture.year === year - 1) && (
            <div className="no-cultures-message">{t('simulation.common.noCulturesForYear')}</div>
          )}
          {culturesByYear?.some((culture) => culture.year === year - 1) && (
            <table
              className={
                isDynamic ? 'cultures-map-tab-table dynamic-table' : 'cultures-map-tab-table'
              }
            >
              <thead
                className={
                  fromProduction ? 'table_head sticky-table' : 'table_head table-synthesis-sticky'
                }
              >
                <tr>
                  {header.map((header) => (
                    <th
                      key={header}
                      scope="col"
                      className={
                        header === t('column.marginPerHa') ? 'last_column_name' : 'column_name'
                      }
                      colSpan={
                        header === t('simulation.common.margin') || header === t('common.surface')
                          ? 2
                          : 1
                      }
                    >
                      <span>{header}</span>
                    </th>
                  ))}
                </tr>
                {header.includes(t('common.surface')) && (
                  <tr className="column_subName">
                    <th colSpan={2}></th>
                    <th colSpan={1}>(ha)</th>
                    <th colSpan={1}>(%)</th>
                    {header.includes(t('column.marginPerHa')) && <th colSpan={2}></th>}
                  </tr>
                )}
                {header.includes(t('simulation.common.margin')) && (
                  <tr className="column_subName">
                    <th colSpan={2}></th>
                    <th colSpan={1}>(€)</th>
                    <th colSpan={1}>(€ / ha)</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {(() => {
                  const sortedCultures = sortList(
                    culturesByYear,
                    fromProduction ? year + (yearIndex ?? 0) - 1 : year - 1,
                  )?.filter((culture) => culture.id !== undefined);

                  const mainCultures =
                    sortedCultures?.filter((culture) => culture.type !== 'intermediate') || [];
                  const intermediateCultures =
                    sortedCultures?.filter((culture) => culture.type === 'intermediate') || [];

                  const renderCultureRow = (culture, i) => {
                    const isSelectable = !!onSelectCulture;
                    const isSelected = isSelectable && culture.id === selectedCultureId;
                    return (
                      <tr
                        key={i}
                        className="tr"
                        style={{
                          opacity: parcelHoverCultureOpacity(culture.name, parcelHoverCultureName),
                          ...(isSelectable && { cursor: 'pointer' }),
                          ...(isSelected && { backgroundColor: '#d3e8d8', fontWeight: 600 }),
                        }}
                        {...(isSelectable && {
                          onClick: () => onSelectCulture(isSelected ? null : culture.id),
                        })}
                        {...(isDynamic && {
                          onMouseEnter: () => cultureHover(culture.name, parentCallback),
                          onMouseLeave: () => cultureHover('null', parentCallback),
                        })}
                      >
                        <td key={getColor(culture.name)}>
                          <div
                            className="culture-color"
                            style={{
                              background: '#' + getColor(culture.name),
                              ...(isSelectable && {
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }),
                              ...(isSelected && {
                                boxShadow: '0 0 0 2px white, 0 0 0 4px #277134',
                              }),
                            }}
                          >
                            {isSelected && (
                              <CheckIcon
                                sx={{
                                  fontSize: 16,
                                  color: getContrastColor(getColor(culture.name) ?? 'ffffff'),
                                }}
                              />
                            )}
                          </div>
                        </td>

                        {fromProduction && (
                          <>
                            <td className="culture-line-clamp" key={culture.name}>
                              {culture.name}{' '}
                              {culture?.organicState === 'conversion' ? (
                                <SyncAltIcon className="icon" />
                              ) : culture?.organicState === 'organic' ? (
                                <img src={bioLogo} alt={t('alt.bioLogo')} className="icon" />
                              ) : null}
                            </td>
                            <td key={'marge_total'}>
                              {getCultureGrossMargin(
                                culture,
                                previousCropMargin,
                                (yearIndex ?? 0) - 1,
                                suggestions,
                              )}
                            </td>
                            <td key={'marge_ha'}>
                              {getCultureGrossMargin(
                                culture,
                                previousCropMargin,
                                (yearIndex ?? 0) - 1,
                                suggestions,
                                culture.surface,
                              )}
                            </td>
                          </>
                        )}
                        {(fromSynthesis || fromParcels) && (
                          <>
                            <td key={culture.name}>
                              {culture.name ?? t('message.noCultureAssociated')}
                            </td>
                            <td key={culture.surface}>{Math.round(culture.surface * 100) / 100}</td>
                            <td key={'percent'}>{getPercentage(culture.surface, totalSurface)}</td>
                            {fromSynthesis && (
                              <td key={'marge'}>
                                {getCultureGrossMargin(
                                  culture,
                                  previousCropMargin,
                                  yearIndex,
                                  suggestions,
                                  culture.surface,
                                ) ?? '-'}
                              </td>
                            )}
                          </>
                        )}
                      </tr>
                    );
                  };

                  return (
                    <React.Fragment>
                      {mainCultures.map((culture, i) => renderCultureRow(culture, i))}

                      {mainCultures.length > 0 && !hideSubtotals && (
                        <tr className="subtotal-row">
                          <td></td>
                          <td className="subtotal-label">{t('title.mainCulturesSubtotal')}</td>
                          <td className="subtotal-value">
                            {Math.round(
                              mainCultures.reduce((sum, c) => sum + (c.surface || 0), 0) * 100,
                            ) / 100}
                          </td>
                          <td></td>
                          {fromSynthesis && <td></td>}
                        </tr>
                      )}

                      {intermediateCultures.length > 0 && (
                        <tr className="section-header-row">
                          <th colSpan={header.length + 1} className="section-header">
                            {t('common.interculture')}
                          </th>
                        </tr>
                      )}

                      {intermediateCultures.map((culture, i) =>
                        renderCultureRow(culture, mainCultures.length + i),
                      )}

                      {intermediateCultures.length > 0 && (
                        <tr className="subtotal-row subtotal-row--intermediate">
                          <td></td>
                          <td className="subtotal-label">{t('title.interculturesSubtotal')}</td>
                          <td className="subtotal-value">
                            {Math.round(
                              intermediateCultures.reduce((sum, c) => sum + (c.surface || 0), 0) *
                                100,
                            ) / 100}
                          </td>
                          <td></td>
                          {fromSynthesis && <td></td>}
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })()}
              </tbody>
            </table>
          )}
        </div>
        {footer}
      </div>
    </div>
  );
};

export default MapTab;
